/**
 * routes/index.js — routing and route guards.
 *
 * DEFECTS IN v1 THAT THIS FIXES
 *  - Zero code splitting: React.lazy and Suspense appeared 0 times, so every
 *    screen plus react-quill, react-barcode, html-to-image, react-to-pdf and a
 *    states-and-districts JSON file were all in the initial bundle, downloaded
 *    before the login screen rendered.
 *  - The sidebar linked to /billdesk/reports, /billdesk/online-store and
 *    /billdesk/manage-users; none were registered, so all three landed on a
 *    bare `<h1>Page Not Found</h1>`.
 *  - ProtectedRoute accepted an `allowedRoles` prop and never read it, and no
 *    caller ever passed one — every route was equally accessible to every role.
 *  - Guards evaluated before the auth token had resolved (loading was gated on
 *    a 1.5s timer), so a valid user with a slow token was redirected to the
 *    landing page.
 *  - `IoMagnet` was imported and never used.
 */

import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { routeRefusal } from '../lib/roles';
import { ErrorState, LoadingBlock } from '../components/ui';
import ErrorBoundary from '../components/ErrorBoundary';
import AppShell from '../components/layout/AppShell';

/* Every screen is lazily loaded. The login screen ships in the initial
   bundle because it is the first thing an unauthenticated visitor needs. */
import SignIn from '../features/auth/SignIn';

const Onboarding = lazy(() => import('../features/onboarding/Onboarding'));
const Dashboard = lazy(() => import('../features/dashboard/Dashboard'));

const PartyList = lazy(() => import('../features/parties/PartyList'));
const PartyForm = lazy(() => import('../features/parties/PartyForm'));
const PartyDetail = lazy(() => import('../features/parties/PartyDetail'));

const ItemList = lazy(() => import('../features/items/ItemList'));
const ItemForm = lazy(() => import('../features/items/ItemForm'));
const ItemDetail = lazy(() => import('../features/items/ItemDetail'));
/* The suggested-item catalogues are ~600 rows of data. Lazily loaded like
   every other screen, so they never reach a user who does not open them. */
const CatalogueImport = lazy(() => import('../features/catalogue/CatalogueImport'));
const BarcodeLabels = lazy(() => import('../features/items/BarcodeLabels'));

const VoucherList = lazy(() => import('../features/vouchers/VoucherList'));
const VoucherEditor = lazy(() => import('../features/vouchers/VoucherEditor'));
const VoucherDetail = lazy(() => import('../features/vouchers/VoucherDetail'));

const PaymentList = lazy(() => import('../features/payments/PaymentList'));
const AccountList = lazy(() => import('../features/accounts/AccountList'));
const AccountDetail = lazy(() => import('../features/accounts/AccountDetail'));
const ChequeRegister = lazy(() => import('../features/accounts/ChequeRegister'));

/**
 * The general ledger.
 *
 * Lazily loaded like everything else, which matters more here than elsewhere:
 * a counter user who only ever bills never downloads the accounting module at
 * all, and the module is the largest single feature in the application.
 */
const ChartOfAccounts = lazy(() => import('../features/accounting/ChartOfAccounts'));
const LedgerStatement = lazy(() => import('../features/accounting/LedgerStatement'));
const AccountingVoucherList = lazy(() => import('../features/accounting/AccountingVoucherList'));
const AccountingVoucherEditor = lazy(() =>
  import('../features/accounting/AccountingVoucherEditor')
);
const AccountingVoucherDetail = lazy(() =>
  import('../features/accounting/AccountingVoucherDetail')
);
const DayBook = lazy(() => import('../features/accounting/DayBook'));
const OutstandingReport = lazy(() => import('../features/accounting/OutstandingReport'));
const TrialBalanceReport = lazy(() => import('../features/reports/TrialBalanceReport'));
const GlBalanceSheetReport = lazy(() => import('../features/reports/GlBalanceSheetReport'));
const GlProfitLossReport = lazy(() => import('../features/reports/GlProfitLossReport'));
const GlCashFlowReport = lazy(() => import('../features/reports/GlCashFlowReport'));
const BankReconciliation = lazy(() => import('../features/accounts/BankReconciliation'));
const OutboxScreen = lazy(() => import('../features/messaging/OutboxScreen'));
const WarehouseList = lazy(() => import('../features/inventory/WarehouseList'));
const StockTakeScreen = lazy(() => import('../features/inventory/StockTakeScreen'));
const PosScreen = lazy(() => import('../features/pos/PosScreen'));
const PriceBookScreen = lazy(() => import('../features/pricing/PriceBookScreen'));
const PriceListEditor = lazy(() => import('../features/pricing/PriceListEditor'));
const RemindersScreen = lazy(() => import('../features/reminders/RemindersScreen'));
const ActivityScreen = lazy(() => import('../features/audit/ActivityScreen'));
const ManufacturingScreen = lazy(() => import('../features/manufacturing/ManufacturingScreen'));
const BomEditor = lazy(() => import('../features/manufacturing/BomEditor'));
const DataScreen = lazy(() => import('../features/data/DataScreen'));
const ImportScreen = lazy(() => import('../features/data/ImportScreen'));
const RecurringScreen = lazy(() => import('../features/recurring/RecurringScreen'));
const StoreSettingsScreen = lazy(() => import('../features/store/StoreSettingsScreen'));
const PublicStore = lazy(() => import('../features/store/PublicStore'));
const Reports = lazy(() => import('../features/reports/Reports'));
const Settings = lazy(() => import('../features/settings/Settings'));
const NotFound = lazy(() => import('../features/NotFound'));

/**
 * The store's old address, `/s/:slug`.
 *
 * A published store's URL is printed on invoice footers, pasted into WhatsApp
 * and indexed by search engines. It belongs to whoever holds it, and there is
 * no way to reach those people to tell them it moved — so the old path keeps
 * resolving, permanently, rather than becoming a 404 the shop owner only finds
 * out about from a customer.
 *
 * `replace` so the dead address does not sit in the visitor's back button.
 */
function LegacyStoreRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/store/${slug || ''}`} replace />;
}

/* ------------------------------------------------------------------ *
 * Guards
 * ------------------------------------------------------------------ */

function FullPageLoader({ label }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <LoadingBlock label={label} />
    </div>
  );
}

/**
 * A route the signed-in role may not open.
 *
 * Shown as an explanation rather than a redirect. A redirect to the dashboard
 * leaves somebody who followed a link from a colleague wondering whether the
 * link was wrong, whether the page was deleted or whether the app is broken —
 * three guesses, none of them the truth. The truth is one sentence.
 */
function RouteRefused({ reason }) {
  return (
    <div className="stack gap-4 mt-5">
      <ErrorState
        title="Not Available to Your Role"
        message={reason}
        onRetry={null}
      />
    </div>
  );
}

/**
 * Gate a screen on the capability its path requires.
 *
 * The table lives in lib/roles, in ONE place, because the failure mode of
 * per-screen checks is exactly what this application had: eight of about
 * thirty screens checked and the rest open. A path with no entry is allowed —
 * see the note on `canOpenRoute` for why forgiving is right there and strict
 * is right in the table.
 */
function Guarded({ path, children }) {
  const { claims } = useAuth();
  const reason = routeRefusal(claims?.role || '', path);
  if (reason) return <RouteRefused reason={reason} />;
  return children;
}

/** Requires a signed-in user with a provisioned business. */
function RequireBusiness({ children }) {
  const { status } = useAuth();
  const location = useLocation();

  // Wait for the token. v1 evaluated the guard against a timer instead.
  if (status === 'initialising') return <FullPageLoader label="Signing you in…" />;
  if (status === 'signed-out') return <Navigate to="/sign-in" replace state={{ from: location }} />;
  if (status === 'needs-setup') return <Navigate to="/setup" replace />;
  return children;
}

/** The onboarding route: signed in, but no business yet. */
function RequireSetup({ children }) {
  const { status } = useAuth();
  if (status === 'initialising') return <FullPageLoader label="Loading…" />;
  if (status === 'signed-out') return <Navigate to="/sign-in" replace />;
  if (status === 'ready') return <Navigate to="/dashboard" replace />;
  return children;
}

/** The sign-in route: bounce an already-authenticated user onward. */
function RequireAnonymous({ children }) {
  const { status } = useAuth();
  if (status === 'initialising') return <FullPageLoader label="Loading…" />;
  if (status === 'ready') return <Navigate to="/dashboard" replace />;
  if (status === 'needs-setup') return <Navigate to="/setup" replace />;
  return children;
}

/* ------------------------------------------------------------------ *
 * Routes
 * ------------------------------------------------------------------ */

/**
 * Every lazy screen sits inside both a Suspense boundary and its own error
 * boundary, so one broken screen cannot white-screen the whole application.
 */
function Screen({ children }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingBlock />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/sign-in"
        element={
          <RequireAnonymous>
            <SignIn />
          </RequireAnonymous>
        }
      />

      {/* The shop window. OUTSIDE every guard, and outside the AppShell: a
          catalogue behind a sign-in is not a shop window, and a visitor must
          not see a sidebar for an application they have no account in. */}
      <Route path="/store/:slug" element={<Screen><PublicStore /></Screen>} />

      {/* The address this used to live at.
          A published store's URL is printed on invoices, pasted into WhatsApp
          and indexed by search engines — it belongs to whoever has it, not to
          us, and quietly breaking it would take a shop's window offline with
          no way for the owner to know why. `replace` so the old address does
          not sit in the visitor's history. */}
      <Route path="/s/:slug" element={<LegacyStoreRedirect />} />

      <Route
        path="/setup"
        element={
          <RequireSetup>
            <Screen>
              <Onboarding />
            </Screen>
          </RequireSetup>
        }
      />

      <Route
        element={
          <RequireBusiness>
            <AppShell />
          </RequireBusiness>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Guarded path="/dashboard"><Screen><Dashboard /></Screen></Guarded>} />
        <Route path="/pos" element={<Guarded path="/pos"><Screen><PosScreen /></Screen></Guarded>} />

        <Route path="/parties" element={<Guarded path="/parties"><Screen><PartyList /></Screen></Guarded>} />
        <Route path="/parties/new" element={<Guarded path="/parties/new"><Screen><PartyForm /></Screen></Guarded>} />
        <Route path="/parties/:partyId" element={<Guarded path="/parties/:partyId"><Screen><PartyDetail /></Screen></Guarded>} />
        <Route path="/parties/:partyId/edit" element={<Guarded path="/parties/:partyId/edit"><Screen><PartyForm /></Screen></Guarded>} />

        <Route path="/items" element={<Guarded path="/items"><Screen><ItemList /></Screen></Guarded>} />
        <Route path="/items/new" element={<Guarded path="/items/new"><Screen><ItemForm /></Screen></Guarded>} />
        {/* Sits alongside /items/:itemId. React Router v6 ranks a static
            segment above a dynamic one, so "catalogue" is never read as an
            item id — but it is kept next to /items/new so the pair is
            obvious to the next person reading this table. */}
        <Route path="/items/catalogue" element={<Guarded path="/items/catalogue"><Screen><CatalogueImport /></Screen></Guarded>} />
        <Route path="/items/labels" element={<Guarded path="/items/labels"><Screen><BarcodeLabels /></Screen></Guarded>} />
        <Route path="/items/:itemId" element={<Guarded path="/items/:itemId"><Screen><ItemDetail /></Screen></Guarded>} />
        <Route path="/items/:itemId/edit" element={<Guarded path="/items/:itemId/edit"><Screen><ItemForm /></Screen></Guarded>} />

        <Route path="/locations" element={<Guarded path="/locations"><Screen><WarehouseList /></Screen></Guarded>} />
        <Route path="/stock-take" element={<Guarded path="/stock-take"><Screen><StockTakeScreen /></Screen></Guarded>} />

        {/* One set of routes for all five voucher types, parameterised by
            the URL segment. v1 had five near-identical copies of each. */}
        <Route path="/vouchers/:voucherRoute" element={<Guarded path="/vouchers/:voucherRoute"><Screen><VoucherList /></Screen></Guarded>} />
        <Route path="/vouchers/:voucherRoute/new" element={<Guarded path="/vouchers/:voucherRoute/new"><Screen><VoucherEditor /></Screen></Guarded>} />
        <Route path="/vouchers/:voucherRoute/:voucherId" element={<Guarded path="/vouchers/:voucherRoute/:voucherId"><Screen><VoucherDetail /></Screen></Guarded>} />

        <Route path="/payments" element={<Guarded path="/payments"><Screen><PaymentList /></Screen></Guarded>} />
        <Route path="/accounts" element={<Guarded path="/accounts"><Screen><AccountList /></Screen></Guarded>} />
        {/* Static before dynamic, like /items/catalogue: React Router ranks a
            literal segment above a parameter, so "cheques" is never read as an
            account id — but the pair is kept adjacent so that is obvious. */}
        <Route path="/accounts/cheques" element={<Guarded path="/accounts/cheques"><Screen><ChequeRegister /></Screen></Guarded>} />
        <Route path="/accounts/:accountId/reconcile" element={<Guarded path="/accounts/:accountId/reconcile"><Screen><BankReconciliation /></Screen></Guarded>} />
        <Route path="/accounts/:accountId" element={<Guarded path="/accounts/:accountId"><Screen><AccountDetail /></Screen></Guarded>} />
        {/* The general ledger.

            Static segments before the parameterised ones, as everywhere else in
            this table: React Router ranks a literal above a parameter, so
            "day-book" is never read as a ledger id — but they are kept adjacent
            so that is obvious to whoever reads this next. */}
        <Route path="/accounting" element={<Guarded path="/accounting"><Screen><ChartOfAccounts /></Screen></Guarded>} />
        <Route path="/accounting/day-book" element={<Guarded path="/accounting/day-book"><Screen><DayBook /></Screen></Guarded>} />
        <Route path="/accounting/trial-balance" element={<Guarded path="/accounting/trial-balance"><Screen><TrialBalanceReport /></Screen></Guarded>} />
        <Route path="/accounting/balance-sheet" element={<Guarded path="/accounting/balance-sheet"><Screen><GlBalanceSheetReport /></Screen></Guarded>} />
        <Route path="/accounting/profit-loss" element={<Guarded path="/accounting/profit-loss"><Screen><GlProfitLossReport /></Screen></Guarded>} />
        <Route path="/accounting/cash-flow" element={<Guarded path="/accounting/cash-flow"><Screen><GlCashFlowReport /></Screen></Guarded>} />
        <Route path="/accounting/outstanding" element={<Guarded path="/accounting/outstanding"><Screen><OutstandingReport /></Screen></Guarded>} />
        <Route path="/accounting/ledgers/:ledgerId" element={<Guarded path="/accounting/ledgers/:ledgerId"><Screen><LedgerStatement /></Screen></Guarded>} />
        <Route path="/accounting/vouchers/:voucherRoute" element={<Guarded path="/accounting/vouchers/:voucherRoute"><Screen><AccountingVoucherList /></Screen></Guarded>} />
        <Route path="/accounting/vouchers/:voucherRoute/new" element={<Guarded path="/accounting/vouchers/:voucherRoute/new"><Screen><AccountingVoucherEditor /></Screen></Guarded>} />
        <Route path="/accounting/vouchers/:voucherRoute/:voucherId" element={<Guarded path="/accounting/vouchers/:voucherRoute/:voucherId"><Screen><AccountingVoucherDetail /></Screen></Guarded>} />
        <Route path="/accounting/vouchers/:voucherRoute/:voucherId/edit" element={<Guarded path="/accounting/vouchers/:voucherRoute/:voucherId/edit"><Screen><AccountingVoucherEditor /></Screen></Guarded>} />
        {/* Static before dynamic, as with /items/catalogue: React Router ranks
            a literal segment above a parameter. */}
        <Route path="/pricing" element={<Guarded path="/pricing"><Screen><PriceBookScreen /></Screen></Guarded>} />
        <Route path="/pricing/:priceListId" element={<Guarded path="/pricing/:priceListId"><Screen><PriceListEditor /></Screen></Guarded>} />

        <Route path="/manufacturing" element={<Guarded path="/manufacturing"><Screen><ManufacturingScreen /></Screen></Guarded>} />
        <Route path="/manufacturing/boms/:bomId" element={<Guarded path="/manufacturing/boms/:bomId"><Screen><BomEditor /></Screen></Guarded>} />

        <Route path="/recurring" element={<Guarded path="/recurring"><Screen><RecurringScreen /></Screen></Guarded>} />
        <Route path="/reminders" element={<Guarded path="/reminders"><Screen><RemindersScreen /></Screen></Guarded>} />
        <Route path="/messages" element={<Guarded path="/messages"><Screen><OutboxScreen /></Screen></Guarded>} />
        <Route path="/activity" element={<Guarded path="/activity"><Screen><ActivityScreen /></Screen></Guarded>} />
        <Route path="/data" element={<Guarded path="/data"><Screen><DataScreen /></Screen></Guarded>} />
        <Route path="/data/import" element={<Guarded path="/data/import"><Screen><ImportScreen /></Screen></Guarded>} />
        <Route path="/store" element={<Guarded path="/store"><Screen><StoreSettingsScreen /></Screen></Guarded>} />

        <Route path="/reports" element={<Guarded path="/reports"><Screen><Reports /></Screen></Guarded>} />
        <Route path="/settings" element={<Guarded path="/settings"><Screen><Settings /></Screen></Guarded>} />
        <Route path="/settings/:section" element={<Guarded path="/settings/:section"><Screen><Settings /></Screen></Guarded>} />
      </Route>

      {/* Legacy v1 URLs, so existing bookmarks keep working. */}
      <Route path="/billdesk/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/billdesk/parties" element={<Navigate to="/parties" replace />} />
      <Route path="/billdesk/items" element={<Navigate to="/items" replace />} />
      <Route path="/billdesk/vouchers/sales" element={<Navigate to="/vouchers/sales" replace />} />
      <Route path="/billdesk/vouchers/purchases" element={<Navigate to="/vouchers/purchases" replace />} />
      <Route path="/billdesk/vouchers/sales-return" element={<Navigate to="/vouchers/sales-return" replace />} />
      <Route path="/billdesk/vouchers/purchase-return" element={<Navigate to="/vouchers/purchase-return" replace />} />
      <Route path="/billdesk/vouchers/expenses" element={<Navigate to="/vouchers/expenses" replace />} />
      <Route path="/billdesk/payment-receipts" element={<Navigate to="/payments" replace />} />
      <Route path="/billdesk/settings" element={<Navigate to="/settings" replace />} />
      {/* Three v1 sidebar links that were never registered as routes and 404'd.
          Two of them now exist; the third arrives with the online store. */}
      <Route path="/billdesk/manage-users" element={<Navigate to="/settings/team" replace />} />
      <Route path="/billdesk/reports" element={<Navigate to="/reports" replace />} />
      <Route path="/billdesk/*" element={<Navigate to="/dashboard" replace />} />

      <Route path="*" element={<Screen><NotFound /></Screen>} />
    </Routes>
  );
}
